import { volumeUnit, type DisplayUnit } from '../lib/units.ts'

/** Volume display unit chosen once per chart from its largest value. */
export type VolumeUnit = DisplayUnit

/** Litres → display unit, in whichever unit system the viewer chose. */
export function pickVolumeUnit(maxLitres: number): VolumeUnit {
  return volumeUnit(maxLitres)
}

export function formatVolume(litres: number, unit: VolumeUnit): string {
  const v = litres * unit.scale
  return `${formatNumber(v, unit.decimals)} ${unit.suffix}`
}

export function formatNumber(value: number, decimals = 0): string {
  return value.toLocaleString('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

/** Compact form for a hero figure or stat tile: 1,284 · 12.9K · 4.2M. */
export function formatCompact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${formatNumber(value / 1_000_000, 1)}M`
  if (abs >= 10_000) return `${formatNumber(value / 1000, 1)}K`
  return formatNumber(value, abs < 10 && abs > 0 ? 1 : 0)
}

export function formatPercent(value: number, decimals = 1): string {
  return `${formatNumber(value, decimals)}%`
}

/** Axis ticks land on 1/2/5 × 10ⁿ so the labels read as round numbers. */
export function niceTicks(max: number, target = 5): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0, 1]
  const raw = max / target
  const mag = 10 ** Math.floor(Math.log10(raw))
  const norm = raw / mag
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * mag
  // The last tick must sit at or above the max, or the axis clips the peak.
  const ticks: number[] = []
  for (let t = 0; ticks.length < 100; t += step) {
    ticks.push(Number(t.toFixed(10)))
    if (t >= max - step * 1e-9) break
  }
  return ticks.length >= 2 ? ticks : [0, step]
}

/** Ticks spanning a signed range, zero always among them, same step both sides. */
export function niceTicksRange(min: number, max: number, target = 5): number[] {
  const span = Math.max(Math.abs(min), Math.abs(max))
  const step = niceTicks(span, Math.max(2, Math.ceil(target / 2)))[1] ?? 1
  const lo = min < 0 ? -Math.ceil(Math.abs(min) / step - 1e-9) * step : 0
  const hi = max > 0 ? Math.ceil(max / step - 1e-9) * step : 0
  const ticks: number[] = []
  for (let t = lo; t <= hi + step * 0.001; t += step) ticks.push(Number(t.toFixed(10)))
  return ticks.length >= 2 ? ticks : [0, step]
}

export function formatSigned(value: number, decimals = 0): string {
  if (Number.isNaN(value)) return '—'
  // Sign follows the rounded figure: 0.04 L at one decimal is 0.0, not +0.0.
  const rounded = Number(Math.abs(value).toFixed(decimals))
  const sign = rounded === 0 ? '' : value > 0 ? '+' : '−'
  return `${sign}${formatNumber(rounded, decimals)}`
}
